// ─── Guided Setup: restricted areas ───────────────────────────────────────────
//
// detector_config.zone_intrusion, per camera. Two shapes, and the difference is
// the question worth asking:
//
//   AREA      a polygon nobody should be standing in — a substation, a chemical
//             store, the space in front of a press. Membership is the person's
//             CENTROID inside the polygon, which is the same rule every zone in
//             this system uses, so draw it around where a person's middle will
//             be, not around the floor they stand on.
//   BOUNDARY  a line nobody should cross, guarded on one side. Right for a
//             perimeter or a doorway where the far side is out of bounds.
//
// One per camera — the config holds a single zone, so a camera is either an area
// or a boundary. Said out loud, because a second zone silently replacing the
// first would be a trap.
//
// authorized_names is the staff exemption: with names set, a recognised person on
// the list is let through and everybody else alerts. Identity is optional — with
// no names, any person in the zone alerts.

import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../../utils/streamApi';
import PatrolZoneEditor from '../PatrolZoneEditor';
import { Field, Panel, Choice, SettingRow, Advanced, countChanged, useModuleSaver }
  from './SetupBits';

const DETECTOR = 'zone_intrusion';

// `def` matches ZoneIntrusionDetector's own defaults.
const ADVANCED = [
  { key: 'dwell_secs', label: 'Seconds inside before it alerts', type: 'range',
    min: 0, max: 60, step: 1, def: 0,
    hint: 'Zero alerts the moment somebody is inside. Raise it where people legitimately pass through the edge of the area.' },
  { key: 'persist_frames', label: 'Frames before it counts', type: 'int',
    min: 1, max: 15, step: 1, def: 3 },
  { key: 'cooldown_secs', label: 'Quiet period after an alert (s)', type: 'int',
    min: 0, max: 600, step: 10, def: 20 },
  { key: 'auth_face_threshold', label: 'Confidence to accept an allowed face', type: 'range',
    min: 0.3, max: 0.9, step: 0.05, def: 0.55 },
];

const blank = () => ({
  mode: 'zone', object: 'person', polygon: null, line: null, invert: false,
  authorized_names: [],
  ...Object.fromEntries(ADVANCED.map(s => [s.key, s.def])),
});

export default function ZonesPanel({ cameras, onChanged }) {
  const [cams,  setCams]  = useState(null);
  const [known, setKnown] = useState([]);
  const [edit,  setEdit]  = useState(null);      // { idx, ...config }
  const { busy, err, note, setErr, run } = useModuleSaver(apiFetch, onChanged);

  const load = useCallback(() => apiFetch('/ui/cameras')
    .then(r => (r.ok ? r.json() : null))
    .then(d => { if (d) setCams(d.cameras || []); })
    .catch(() => setErr('Could not read the cameras.')), [setErr]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    apiFetch('/faces/known')
      .then(r => (r.ok ? r.json() : null))
      .then(d => setKnown((d?.persons || []).map(p => p.name)))
      .catch(() => {});
  }, []);

  const rows = (cams || [])
    .filter(c => cameras.some(x => x.idx === c.idx))
    .map(c => ({
      idx: c.idx,
      name: c.name || `Camera ${c.idx}`,
      on: (c.detectors || []).includes(DETECTOR),
      cfg: (c.detector_config || {})[DETECTOR] || null,
    }));

  const shaped = r => {
    const z = r.cfg;
    if (!z) return null;
    if (z.mode === 'boundary') return (z.line || []).length === 2 ? 'boundary' : null;
    return (z.polygon || []).length >= 3 ? 'zone' : null;
  };

  async function save() {
    const isZone  = edit.mode === 'zone';
    const payload = {
      mode: edit.mode, object: 'person',
      polygon: isZone ? edit.polygon : null,
      line:    isZone ? null : edit.line,
      invert:  isZone ? false : !!edit.invert,
      authorized_names: edit.authorized_names,
      ...Object.fromEntries(ADVANCED.map(s => [s.key, edit[s.key]])),
    };
    const cam  = (cams || []).find(c => c.idx === edit.idx);
    const dets = [...new Set([...(cam?.detectors || []), DETECTOR])];
    const d = await run('PUT', `/cameras/${edit.idx}`,
                        { detectors: dets, detector_config: { [DETECTOR]: payload } },
                        'this area');
    if (d) { setEdit(null); await load(); }
  }

  async function remove(idx) {
    if (!window.confirm('Stop watching this area? The drawing is kept, the alerting stops.')) return;
    const cam  = (cams || []).find(c => c.idx === idx);
    const dets = (cam?.detectors || []).filter(d => d !== DETECTOR);
    if (await run('PUT', `/cameras/${idx}`, { detectors: dets }, 'the change')) await load();
  }

  const drawn = edit && (edit.mode === 'zone'
    ? (edit.polygon || []).length >= 3
    : (edit.line || []).length === 2);

  // ── draw / edit one camera ──
  if (edit) {
    const cam = rows.find(r => r.idx === edit.idx);
    return (
      <>
        <div className="gs-section-title">Restricted area on {cam?.name}</div>

        <Field label="What kind?">
          <div className="gs-choices">
            <Choice icon="⬛" title="An area" sub="Nobody should be standing inside it"
                    selected={edit.mode === 'zone'}
                    onClick={() => setEdit(e => ({ ...e, mode: 'zone' }))} />
            <Choice icon="↔️" title="A line" sub="Nobody should cross it"
                    selected={edit.mode === 'boundary'}
                    onClick={() => setEdit(e => ({ ...e, mode: 'boundary' }))} />
          </div>
        </Field>

        {edit.mode === 'zone' ? (
          <Field
            label="Draw the area"
            help="Tap its corners. Somebody counts as inside when the MIDDLE of them is inside the shape, so draw it around where a person’s body will be rather than tight to the floor."
          >
            <PatrolZoneEditor cameraIdx={edit.idx} value={edit.polygon || []}
                              onChange={p => setEdit(e => ({ ...e, polygon: p }))} />
          </Field>
        ) : (
          <>
            <Field label="Draw the line" help="Tap the two ends of it.">
              <PatrolZoneEditor cameraIdx={edit.idx} shape="line" value={edit.line || []}
                                onChange={p => setEdit(e => ({ ...e, line: p }))} />
            </Field>
            <SettingRow
              spec={{ key: 'invert', label: 'Guard the other side', type: 'bool', def: false,
                      hint: 'Which side is out of bounds. If alerts come when somebody walks the wrong way, this is the switch.' }}
              value={edit.invert}
              onChange={v => setEdit(e => ({ ...e, invert: v }))} />
          </>
        )}

        {known.length > 0 && (
          <Field
            label="Who is allowed in anyway?"
            help="Recognised people on this list are let through. Leave it empty and ANY person in the area alerts, which is usually what a restricted area means."
          >
            <div className="gs-chips">
              {known.map(n => (
                <button key={n} type="button"
                        className={`gs-chip${edit.authorized_names.includes(n) ? ' on' : ''}`}
                        onClick={() => setEdit(e => ({
                          ...e,
                          authorized_names: e.authorized_names.includes(n)
                            ? e.authorized_names.filter(x => x !== n)
                            : [...e.authorized_names, n],
                        }))}>
                  {n}
                </button>
              ))}
            </div>
          </Field>
        )}

        <Advanced changed={countChanged(ADVANCED, edit)}>
          {ADVANCED.map(s => (
            <SettingRow key={s.key} spec={s} value={edit[s.key]}
                        onChange={v => setEdit(e => ({ ...e, [s.key]: v }))} />
          ))}
        </Advanced>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
          <button className="gs-btn primary" onClick={save} disabled={busy || !drawn}>
            {busy ? <span className="gs-spin" /> : 'Save this area'}
          </button>
          <button className="gs-btn ghost" onClick={() => { setEdit(null); setErr(null); }}>Cancel</button>
          {!drawn && (
            <span className="gs-foot-note">
              {edit.mode === 'zone' ? 'Draw the area — three corners is enough'
                                    : 'Place both ends of the line'}
            </span>
          )}
        </div>
        {err && <div className="gs-err" role="alert">{err}</div>}
      </>
    );
  }

  // ── the list ──
  return (
    <>
      {!cams && !err && (
        <div className="gs-panel"><div className="gs-scanning"><span className="gs-spin" /> Loading…</div></div>
      )}

      {cams && (
        <>
          <p className="gs-help">
            One area or line per camera. Point a second camera at the same place if you
            need to cover it from two angles — a camera cannot hold two areas, and saving
            a new one replaces what is there.
          </p>

          {rows.map(r => {
            const sh = shaped(r);
            return (
              <div className={`gs-row${r.on && !sh ? ' gs-row-bad' : ''}`} key={r.idx}>
                <span className="gs-row-icon">
                  {!r.on ? '📷' : sh === 'boundary' ? '↔️' : sh === 'zone' ? '⬛' : '⚠️'}
                </span>
                <span className="gs-row-text">
                  <span className="gs-row-name">{r.name}</span>
                  <span className="gs-row-sub">
                    {!r.on ? 'Nothing restricted here'
                      : !sh ? 'Switched on but NOTHING DRAWN — it cannot alert'
                      : sh === 'boundary'
                        ? `A line nobody may cross${r.cfg.authorized_names?.length ? ` · except ${r.cfg.authorized_names.join(', ')}` : ''}`
                        : `An area nobody may enter${r.cfg.authorized_names?.length ? ` · except ${r.cfg.authorized_names.join(', ')}` : ''}`}
                  </span>
                </span>
                <button className="gs-btn small"
                        onClick={() => { setErr(null); setEdit({ ...blank(), ...(r.cfg || {}),
                          authorized_names: r.cfg?.authorized_names || [], idx: r.idx }); }}>
                  {r.on ? 'Change' : 'Set one up'}
                </button>
                {r.on && (
                  <button className="gs-btn small ghost" disabled={busy}
                          onClick={() => remove(r.idx)}>Stop</button>
                )}
              </div>
            );
          })}

          <Panel title="What happens on an intrusion">
            It raises an alert like any other — photo, clip, and your alert devices — so
            there is nothing extra to set up for it. A recognised person on the allowed
            list is let through silently.
          </Panel>

          {note && <p className="gs-help gs-help-after">{note}</p>}
          {err && <div className="gs-err" role="alert">{err}</div>}
        </>
      )}
    </>
  );
}
